import {Component, Inject, Input, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {first, skipUntil, startWith, takeUntil} from 'rxjs/operators';
import {Uuid} from '../../../shared/interface/response/uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {CommissionImportRowService} from '../../../shared/services/commission-import-row.service';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {FilterService} from '../../../shared/services/filter.service';
import {of, ReplaySubject} from "rxjs";
import {Contract} from "../../../shared/interface/contract";
import {Ofwca} from "../../../shared/interface/ofwca";
import {
  COMPANY_ASF_PREMIUM,
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_CANCELED,
  CONTRACT_STATUS_CLOSED,
  CONTRACT_STATUS_DOWNLOADED,
  CONTRACT_STATUS_DURING_TERMINATION,
  CONTRACT_STATUS_HANDED_OVER_REGIONAL,
  CONTRACT_STATUS_NEW,
  CONTRACT_STATUS_SENT_TO_AGENT,
  CONTRACT_STATUS_SENT_WITH_AUTENTI,
  CONTRACT_STATUS_TERMINATED,
  CONTRACT_STATUS_TO_TERMINATE,
  CONTRACT_TYPE_PARTNERSHIP,
  CONTRACT_TYPE_PROFIT,
} from "../../../shared/services/dict.service";
import {RegistrationService} from "../../../shared/services/registration.service";
import {OfwcaRegistrationInfo} from "../../../shared/interface/ofwca-registration-info";
import {ListResponse} from "../../../shared/interface/response/list-response";
import {OfwcaService} from "../../../shared/services/ofwca.service";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";

export interface MassEditionDialogData {
  filters: any;
}

@Component({
  selector: 'app-mass-edition-dialog',
  templateUrl: './mass-edition-dialog.component.html',
  styleUrls: ['./mass-edition-dialog.component.scss']
})
export class MassEditionDialogComponent implements OnInit {
  filters: any = {};

  massEditForm: FormGroup;
  fieldsControl: FormControl;
  internalRiskControl: FormControl;
  riskPackControl: FormControl;
  policyTypeControl: FormControl;
  ofwcaControl: FormControl;
  ratePaidControl: FormControl;
  commissionPaidControl: FormControl;
  rejectedControl: FormControl;
  assignedControl: FormControl;
  processingRequest = false;
  formErrors: any = {};
  ofwcaContracts: ReplaySubject<Contract[]> = new ReplaySubject<Contract[]>(1);
  contractControl: FormControl;
  selectedOfwca?: Ofwca;
  selectedContract?: Contract;
  noContract = false;
  block = false;
  contracts: Contract[] = [];
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<MassEditionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MassEditionDialogData,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private commissionImportRowService: CommissionImportRowService,
    private dialog: MatDialog,
    private filterService: FilterService,
    private registrationService: RegistrationService,
    private ofwcaService: OfwcaService,
    private auth: AuthService,
  ) {
    this.filters = data.filters;
    this.massEditForm = fb.group({
      fields: this.fieldsControl = fb.control([]),
      internalRisk: this.internalRiskControl = fb.control(''),
      riskPack: this.riskPackControl = fb.control(''),
      policyType: this.policyTypeControl = fb.control(''),
      ofwca: this.ofwcaControl = fb.control(''),
      ratePaid: this.ratePaidControl = fb.control(''),
      commissionPaid: this.commissionPaidControl = fb.control(''),
      rejected: this.rejectedControl = fb.control(''),
      assigned: this.assignedControl = fb.control(''),
      contract: this.contractControl = fb.control(''),
    });
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.ofwcaControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.processingRequest = true;
        this.loadContract();
      });
    this.loadAssigned();
    this.fieldsControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe( fieldsValue => {
        if (fieldsValue.length === 0){
          this.block = false;
        } else {
          const uniqueValues = [...new Set(fieldsValue)]; // unikalne wartości
          if (fieldsValue.length > 0 && !(uniqueValues.length === 1 && uniqueValues[0] === 'block')) {
            this.block = true;
            // this.assignedControl.reset();
            // this.assignedControl.disable();

          } else {
            this.fieldsControl.setValue([]);
            this.block = false;
          }
        }
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  sendForm(): void {
    if (!this.massEditForm.valid) {
      this.massEditForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.massEditForm, this.formErrors);
    this.formErrors = {};

    const formData = this.massEditForm.value;
    formData.filters = JSON.stringify(this.filterService.parseFiltersToObject(this.filters));

    this.commissionImportRowService.massEdit(formData)
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.processingRequest = false;

        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(() => this.dialogRef.close());
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.massEditForm, this.formErrors);
          this.snack.open(error.error.error.text, '400', {duration: 2000});
        }
      })
    ;
  }
  loadContract(): void{
    this.registrationService.getOfwcaRegistrationInfo(this.ofwcaControl.value)
      .subscribe((ofwcaRegistrationInfo: OfwcaRegistrationInfo) => {
        const availableContracts = ofwcaRegistrationInfo.contracts.filter((contract: Contract) =>
          [CONTRACT_TYPE_PARTNERSHIP, CONTRACT_TYPE_PROFIT].includes(contract.type?.key) &&
          [
            CONTRACT_STATUS_ACTIVE,
            CONTRACT_STATUS_TO_TERMINATE,
            CONTRACT_STATUS_DURING_TERMINATION,
          ].includes(contract.status?.key)
        );
        this.ofwcaContracts.next(availableContracts);

        let activeContract = null;
        ofwcaRegistrationInfo.contracts.forEach((contract: Contract) => {
          // eslint-disable-next-line max-len
          if ((contract.status?.key === CONTRACT_STATUS_ACTIVE) || (contract.status?.key === CONTRACT_STATUS_TO_TERMINATE) || (contract.status?.key === CONTRACT_STATUS_DURING_TERMINATION)) {
            activeContract = contract;
          }
        });
        if (null !== activeContract) {
          this.noContract = false;
          this.processingRequest = false;
        }else{
          this.noContract = true;
          this.processingRequest = true;
        }
      });
  }
  loadAssigned(): void {
    this.rejectedControl.setValue(false);
    this.assignedControl.setValue(false);
    this.assignedControl.disable();

    this.internalRiskControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(internalRiskVal => {
        if (internalRiskVal) {
          this.assignedControl.enable();
          this.rejectedControl.disable();
          this.fieldsControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe( fieldsValue => {
              if (fieldsValue){
                // this.assignedControl.reset();
                // this.assignedControl.disable();

              }
            });
        } else {
          this.assignedControl.disable();
          this.rejectedControl.enable();

        }
      });

  }
}
